import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap";

import TableContainer from "../../../../Components/Common/TableContainer";
import { formatMoneyFromCents } from "../../../../helpers/money";
import { getProductStockMovements } from "../../../../services/productService";
import { PaginationMeta } from "../../../../types/api";
import { StockMovement, StockMovementType } from "../../../../types/stockMovement";
import { UnitOfMeasure } from "../../../../types/product";

import ProductStockAdjustModal from "./ProductStockAdjustModal";

const PER_PAGE = 15;

interface ProductStockHistoryPanelProps {
  productId: number;
  unitOfMeasure: UnitOfMeasure;
  costPriceCents: number | null;
  currentStock: number;
  canAdjust: boolean;
  onStockAdjusted: () => void;
}

const movementBadgeColor = (type: StockMovementType): string => {
  switch (type) {
    case "sale":
      return "success";
    case "internal_use":
      return "info";
    case "manual_adjustment":
      return "warning";
    case "purchase":
      return "primary";
    default:
      return "secondary";
  }
};

const ProductStockHistoryPanel = ({
  productId,
  unitOfMeasure,
  costPriceCents,
  currentStock,
  canAdjust,
  onStockAdjusted,
}: ProductStockHistoryPanelProps) => {
  const { t, i18n } = useTranslation();

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [movementTypeFilter, setMovementTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);

  const notAvailable = t("studio.products.common.notAvailable");
  const measureLabel = t(`studio.products.measure.${unitOfMeasure}`);

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const formatQuantity = useCallback(
    (quantity: number) => {
      const sign = quantity > 0 ? "+" : "";

      return t("studio.products.stockHistory.table.quantityValue", {
        value: `${sign}${quantity}`,
        measure: measureLabel,
      });
    },
    [measureLabel, t]
  );

  const resolveMovementTypeLabel = useCallback(
    (type: StockMovementType) => t(`studio.products.stockHistory.movementType.${type}`),
    [t]
  );

  const resolveOriginLabel = useCallback(
    (movement: StockMovement) => {
      if (movement.reason) {
        return movement.reason;
      }

      if (movement.origin) {
        return t(`studio.products.stockHistory.origin.${movement.origin}`);
      }

      if (movement.order_id !== null) {
        return t("studio.products.stockHistory.origin.orderWithId", {
          id: movement.order_id,
        });
      }

      return notAvailable;
    },
    [notAvailable, t]
  );

  const fetchMovements = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getProductStockMovements(productId, {
        page,
        per_page: PER_PAGE,
        ...(movementTypeFilter
          ? { movement_type: movementTypeFilter as StockMovementType }
          : {}),
      });

      setMovements(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.stockHistory.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [movementTypeFilter, page, productId, t]);

  useEffect(() => {
    setPage(1);
  }, [movementTypeFilter, productId]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleAdjustSuccess = () => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchMovements();
    }
    onStockAdjusted();
  };

  const totalPages = meta?.last_page ?? 1;

  const columns = useMemo(
    () => [
      {
        header: t("studio.products.stockHistory.table.date"),
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell: { getValue: () => string }) => formatDateTime(cell.getValue()),
      },
      {
        header: t("studio.products.stockHistory.table.type"),
        accessorKey: "movement_type",
        enableColumnFilter: false,
        cell: (cell: { getValue: () => StockMovementType }) => (
          <Badge color={movementBadgeColor(cell.getValue())} className="text-uppercase">
            {resolveMovementTypeLabel(cell.getValue())}
          </Badge>
        ),
      },
      {
        header: t("studio.products.stockHistory.table.quantity"),
        accessorKey: "quantity",
        enableColumnFilter: false,
        cell: (cell: { getValue: () => number }) => {
          const quantity = cell.getValue();
          const className =
            quantity > 0
              ? "text-success fw-medium"
              : quantity < 0
                ? "text-danger fw-medium"
                : "text-muted";

          return <span className={className}>{formatQuantity(quantity)}</span>;
        },
      },
      {
        header: t("studio.products.stockHistory.table.balanceAfter"),
        accessorKey: "balance_after",
        enableColumnFilter: false,
        cell: (cell: { getValue: () => number | null }) => {
          const balance = cell.getValue();

          if (balance === null) {
            return notAvailable;
          }

          return t("studio.products.stockHistory.table.balanceValue", {
            value: balance,
            measure: measureLabel,
          });
        },
      },
      {
        header: t("studio.products.stockHistory.table.originReason"),
        accessorKey: "reason",
        enableColumnFilter: false,
        cell: (cell: { row: { original: StockMovement } }) => (
          <span className="text-muted">{resolveOriginLabel(cell.row.original)}</span>
        ),
      },
    ],
    [
      formatDateTime,
      formatQuantity,
      measureLabel,
      notAvailable,
      resolveMovementTypeLabel,
      resolveOriginLabel,
      t,
    ]
  );

  return (
    <div>
      {costPriceCents !== null ? (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="badge bg-light text-body fs-12 fw-medium">
            {t("studio.products.stockHistory.summary.unitCost", {
              value: formatMoney(costPriceCents),
            })}
          </span>
          <span className="badge bg-light text-body fs-12 fw-medium">
            {t("studio.products.stockHistory.summary.inventoryValue", {
              value: formatMoney(currentStock * costPriceCents),
            })}
          </span>
        </div>
      ) : null}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <Input
          type="select"
          value={movementTypeFilter}
          onChange={(event) => setMovementTypeFilter(event.target.value)}
          className="w-auto"
          aria-label={t("studio.products.stockHistory.filter.type")}
        >
          <option value="">{t("studio.products.stockHistory.filter.allTypes")}</option>
          <option value="sale">{t("studio.products.stockHistory.movementType.sale")}</option>
          <option value="internal_use">
            {t("studio.products.stockHistory.movementType.internal_use")}
          </option>
          <option value="manual_adjustment">
            {t("studio.products.stockHistory.movementType.manual_adjustment")}
          </option>
          <option value="purchase">
            {t("studio.products.stockHistory.movementType.purchase")}
          </option>
        </Input>

        {canAdjust ? (
          <Button color="primary" onClick={() => setAdjustModalOpen(true)}>
            <i className="ri-edit-box-line align-bottom me-1" />
            {t("studio.products.stockHistory.actions.adjust")}
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary">{t("studio.products.loading")}</Spinner>
        </div>
      ) : movements.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-2">{t("studio.products.stockHistory.empty.noData")}</p>
          {canAdjust ? (
            <Button color="soft-primary" onClick={() => setAdjustModalOpen(true)}>
              {t("studio.products.stockHistory.actions.adjustFirst")}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="product-stock-history-table">
            <style>
              {`
                .product-stock-history-table .row.align-items-center.mt-2 {
                  display: none !important;
                }
              `}
            </style>
            <TableContainer
              columns={columns}
              data={movements}
              customPageSize={movements.length || PER_PAGE}
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light"
            />
          </div>

          {meta && totalPages > 1 && (
            <Row className="align-items-center mt-3 g-3">
              <Col sm={6}>
                <p className="text-muted mb-0">
                  {t("studio.products.stockHistory.pagination.showing", {
                    from: meta.from ?? 0,
                    to: meta.to ?? 0,
                    total: meta.total,
                  })}
                </p>
              </Col>
              <Col sm={6}>
                <Pagination className="pagination-separated justify-content-sm-end mb-0">
                  <PaginationItem disabled={page <= 1}>
                    <PaginationLink
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        if (page > 1) {
                          setPage(page - 1);
                        }
                      }}
                    >
                      {t("studio.products.pagination.previous")}
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem active>
                    <PaginationLink href="#">
                      {page} / {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem disabled={page >= totalPages}>
                    <PaginationLink
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        if (page < totalPages) {
                          setPage(page + 1);
                        }
                      }}
                    >
                      {t("studio.products.pagination.next")}
                    </PaginationLink>
                  </PaginationItem>
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}

      <ProductStockAdjustModal
        isOpen={adjustModalOpen}
        productId={productId}
        onClose={() => setAdjustModalOpen(false)}
        onSuccess={handleAdjustSuccess}
      />
    </div>
  );
};

export default ProductStockHistoryPanel;
